/**
 * Mentions module — mock widget loaders.
 */
import type { SeriesKey, WidgetLoaders } from '@/data-sources/contract';
import type { Breakdown } from '@/data-sources/contract/widgets/mentions';

import { aggregate } from '../engine/aggregate';
import { bucketsIn } from '../engine/buckets';
import { dimensionIndex, type DimensionKey } from '../engine/dimensions';
import type { MentionRow } from '../engine/generator';
import { metrics } from '../engine/metrics';
import { scoped } from '../query';
import { comparisonOf, timeSeriesOf } from './shared';

/**
 * Rows → a contract `Breakdown`: mention counts per category, split by one
 * dimension or unsplit (one `mentions` series).
 *
 * Categories keep **first-appearance order** — the order the prototype's own
 * grouped passes (`pivot`, `channelStandings`, `intentCounts`) iterated in —
 * so the presenter's stable ranking breaks ties exactly as the prototype did.
 * Series are the split values present, in fixture order (the engine's
 * `seriesForSplit` rule). A row with no category (`outlet: null`) is skipped,
 * as `pivot` skips it.
 */
export function breakdownOf<C>(
  rows: readonly MentionRow[],
  categoryOf: (row: MentionRow) => string | null,
  toCategory: (key: string) => C,
  splitBy?: DimensionKey,
): Breakdown<C> {
  const groups = new Map<string, Map<string, number>>();
  const present = new Set<string>();
  for (const row of rows) {
    const category = categoryOf(row);
    if (category === null) continue;
    const seriesKey = splitBy ? row[splitBy] : 'mentions';
    present.add(seriesKey);
    let bySeries = groups.get(category);
    if (!bySeries) {
      bySeries = new Map();
      groups.set(category, bySeries);
    }
    bySeries.set(seriesKey, (bySeries.get(seriesKey) ?? 0) + 1);
  }

  const categories = [...groups.keys()];
  const seriesKeys = splitBy
    ? [...present].sort(
        (a, b) => (dimensionIndex[splitBy][a] ?? 0) - (dimensionIndex[splitBy][b] ?? 0),
      )
    : ['mentions'];

  return {
    categories: categories.map(toCategory),
    series: seriesKeys.map((key) => {
      const id: SeriesKey = splitBy ? { key, dimension: splitBy } : { key };
      return { ...id, values: categories.map((category) => groups.get(category)?.get(key) ?? 0) };
    }),
  };
}

const option = (dimension: DimensionKey) => (key: string) => ({ key, dimension });

export const mentionsWidgetLoaders = {
  'mention-volume-over-time': async ({ query }) => {
    const s = scoped(query);
    const volume = aggregate(s.own, { metric: 'mentions', from: s.from, to: s.to });
    const before = comparisonOf(s.ownPrevious, s.previousBounds, volume, { metric: 'mentions' });
    return {
      volume: timeSeriesOf(volume, s.from, s.to, undefined, before),
      total: {
        current: metrics.mentions.reduce(s.own),
        previous: s.ownPrevious ? metrics.mentions.reduce(s.ownPrevious) : null,
      },
      days: bucketsIn(s.from, s.to, 'day').length,
    };
  },

  'mentions-by-platform': async ({ query }) => {
    const s = scoped(query);
    return {
      platforms: breakdownOf(s.own, (row) => row.channel, option('channel'), 'sentiment'),
    };
  },

  'intent-based-mentions': async ({ query }) => {
    const s = scoped(query);
    return {
      intents: breakdownOf(s.own, (row) => row.intent, option('intent')),
      previous: s.ownPrevious
        ? breakdownOf(s.ownPrevious, (row) => row.intent, option('intent'))
        : null,
    };
  },
} satisfies Partial<WidgetLoaders>;
